import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTools,
  faGears,
  faFloppyDisk,
  faFileCirclePlus,
  faEraser,
  faFolderOpen,
  faCheck,
  faGripVertical,
} from '@fortawesome/free-solid-svg-icons';
import mapboxgl from 'mapbox-gl';

import './ToolBox.scss';
import ContextHelp from '../../ContextHelp/ContextHelp';
import QuickZoomControl from '../QuickZoomControl/QuickZoomControl';
import PlacenameTool from '../PlacenameTool/PlacenameTool';
import AppModal from '../../../components/common/AppModal';
import { useTools } from '../../../context/ToolContext';
import { usePermissions } from '../../../context/PermissionContext';
import { navigationConfig } from '../../../components/navigation/navigationConfig';
import { resolveCrossAppUrl } from '../../../utils/crossAppUrlResolver';
import { useOptionalToolboxProfile } from '../../../context/ToolboxProfileContext';
import { type CatalogueTool } from './toolboxCatalogue';
import type { ToolboxProfile } from '../../../types/toolboxProfile';
import { useToolboxEditor } from './useToolboxEditor';
import { useDragReorder } from '../../layerDrawer/hooks/useDragReorder';
import { SaveToolboxModal } from './SaveToolboxModal';
import { LoadToolboxModal } from './LoadToolboxModal';

interface ToolBoxProps {
  map?: mapboxgl.Map | null;
  /**
   * The full tool catalogue (every TOOL_KEY joined to nav metadata), supplied
   * by ToolBoxContainer. Omitted when ToolBox is rendered standalone (legacy
   * path), in which case edit mode is unavailable.
   */
  catalogue?: CatalogueTool[];
}

type NavItem = {
  type: 'link' | 'group' | 'external' | 'cross-app';
  label: string;
  tool?: string;
  href?: string;
  icon?: string;
  requires?: { feature: string; operation: string };
  children?: NavItem[];
};

// Tools that need the map ref to do anything — hidden until the map exists.
const MAP_DEPENDENT_TOOLS = new Set(['quickZoom', 'placenameTool']);

// Pull the "Tools" group out of the shared navigation config so the
// toolbox button list and the top-menu Tools submenu stay in lockstep
// (same order, same items, same permission gates).
function getToolsItems(): NavItem[] {
  const tools = (navigationConfig.main as readonly NavItem[]).find(
    (i) => i.type === 'group' && i.label === 'Tools',
  );
  return (tools?.children as NavItem[]) ?? [];
}

const ToolBox: React.FC<ToolBoxProps> = ({ map, catalogue }) => {
  const { openTool } = useTools();
  const { canDo, isAdmin } = usePermissions();
  const profileCtx = useOptionalToolboxProfile();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuickZoomOpen, setIsQuickZoomOpen] = useState(false);
  const [isPlacenameToolOpen, setIsPlacenameToolOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const [isLoadOpen, setIsLoadOpen] = useState(false);
  // Lets the header "Done editing" checkmark trigger the same in-place save as
  // the edit view's save button, whose editor state lives inside ToolboxEditView.
  const editRef = useRef<ToolboxEditHandle>(null);

  // Map-floating controls — opened by ToolContext dispatching these events.
  // Stays in ToolBox because both controls need the map ref.
  useEffect(() => {
    const handleQuickZoomOpen = () => {
      if (map) setIsQuickZoomOpen(true);
    };
    const handlePlacenameToolOpen = () => {
      if (map) setIsPlacenameToolOpen(true);
    };

    window.addEventListener('intellifire:open-quick-zoom', handleQuickZoomOpen);
    window.addEventListener('intellifire:open-placename-tool', handlePlacenameToolOpen);

    return () => {
      window.removeEventListener('intellifire:open-quick-zoom', handleQuickZoomOpen);
      window.removeEventListener('intellifire:open-placename-tool', handlePlacenameToolOpen);
    };
  }, [map]);

  // The permission-allowed catalogue (every tool the user may enable), derived
  // from the catalogue the container supplies. Empty when rendered standalone.
  const allowedCatalogue = useMemo<CatalogueTool[]>(() => {
    return (catalogue ?? []).filter((t) => {
      if (t.requires && !canDo(t.requires.feature, t.requires.operation)) return false;
      if (MAP_DEPENDENT_TOOLS.has(t.key) && !map) return false;
      return true;
    });
  }, [catalogue, canDo, map]);

  // Whether the customizable toolbox is wired (provider mounted + a profile loaded).
  const profileActive = !!profileCtx?.activeProfile;

  // Legacy fallback rendering — the nav "Tools" group — used until the toolbox
  // profile provider is mounted (so the toolbar never breaks mid-migration).
  const legacyItems = useMemo(() => {
    return getToolsItems().filter((item) => {
      if (item.requires && !canDo(item.requires.feature, item.requires.operation)) return false;
      if (item.tool && MAP_DEPENDENT_TOOLS.has(item.tool) && !map) return false;
      return true;
    });
  }, [canDo, map]);

  // The tools shown when not editing: the active profile's resolved tools
  // (already permission-gated by the context), minus map-dependent ones with no map.
  const displayTools = useMemo<CatalogueTool[]>(() => {
    if (!profileActive) return [];
    return profileCtx!.resolvedTools.filter(
      (t) => !(MAP_DEPENDENT_TOOLS.has(t.key) && !map),
    );
  }, [profileActive, profileCtx, map]);

  const handleLegacyClick = (item: NavItem) => {
    setIsModalOpen(false);
    if (item.type === 'link' && item.tool) {
      openTool(item.tool as any);
    } else if (item.type === 'cross-app' && item.href) {
      const url = resolveCrossAppUrl(item.href);
      if (url) window.location.href = url;
    } else if (item.type === 'external' && item.href) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    }
  };

  const handleToolClick = (key: string) => {
    setIsModalOpen(false);
    openTool(key as any);
  };

  return (
    <div className="toolbox">
      <button className="toolbox-button control-button" title="Launch the Toolbox" onClick={() => setIsModalOpen(true)}>
        <FontAwesomeIcon icon={faTools} />
      </button>
      <AppModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        width={420}
        header={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            <ContextHelp title="Help with Toolbox" context="toolbox-help" />
            <div className="toolbox-title">
              <h2 style={{ margin: 0 }}>Toolbox</h2>
              {profileActive && profileCtx!.activeProfile && (
                <div className="toolbox-subheading">Profile: {profileCtx!.activeProfile.name}</div>
              )}
            </div>
            {profileActive && (
              <div className="toolbox-actions" style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                <button
                  className="toolbox-icon-btn"
                  title={isEditing ? 'Done editing' : 'Edit toolbox'}
                  aria-label={isEditing ? 'Done editing' : 'Edit toolbox'}
                  onClick={() => {
                    if (!isEditing) {
                      setIsEditing(true);
                      return;
                    }
                    // "Done editing" persists the in-progress edits in place.
                    // If the active profile can't be saved in place (e.g. a
                    // global profile without admin), just leave edit mode.
                    if (!editRef.current?.save()) setIsEditing(false);
                  }}
                >
                  <FontAwesomeIcon icon={isEditing ? faCheck : faGears} />
                </button>
                <button
                  className="toolbox-icon-btn"
                  title="Load a saved toolbox"
                  aria-label="Load a saved toolbox"
                  onClick={() => setIsLoadOpen(true)}
                >
                  <FontAwesomeIcon icon={faFolderOpen} />
                </button>
              </div>
            )}
          </div>
        }
      >
        {!profileActive
          ? renderLegacy(legacyItems, handleLegacyClick, () => setIsModalOpen(false))
          : isEditing
            ? (
              <ToolboxEditView
                ref={editRef}
                catalogue={allowedCatalogue}
                activeProfile={profileCtx!.activeProfile!}
                canSaveInPlace={!profileCtx!.activeProfile!.is_global || isAdmin}
                onSaveInPlace={async (tools) => {
                  await profileCtx!.update(profileCtx!.activeProfile!.id, { profile_data: { tools } });
                  setIsEditing(false);
                }}
                onRequestSaveAsNew={() => setIsSaveOpen(true)}
                saveModalSlot={(tools) => (
                  <SaveToolboxModal
                    isOpen={isSaveOpen}
                    onClose={() => setIsSaveOpen(false)}
                    onSaved={() => setIsEditing(false)}
                    tools={tools}
                    isAdmin={isAdmin}
                  />
                )}
              />
            )
            : (
              displayTools.map((tool) => (
                <React.Fragment key={tool.key}>
                  <button className="toolbox-tool-button" onClick={() => handleToolClick(tool.key)}>
                    {tool.icon && <i className={tool.icon} style={{ marginRight: '8px' }} />}
                    {tool.label}
                  </button>
                  <p></p>
                </React.Fragment>
              ))
            )}
      </AppModal>

      {profileActive && (
        <LoadToolboxModal isOpen={isLoadOpen} onClose={() => setIsLoadOpen(false)} isAdmin={isAdmin} />
      )}

      {isQuickZoomOpen && map && createPortal(
        <div className="toolbox-floating-control" style={{ position: 'fixed', top: '100px', left: '60px', zIndex: 9999 }}>
          <QuickZoomControl map={map} />
          <button className="floating-control-close" onClick={() => setIsQuickZoomOpen(false)} title="Close Quick Zoom">
            &times;
          </button>
        </div>,
        document.body,
      )}
      {isPlacenameToolOpen && map && createPortal(
        <div className="toolbox-floating-control" style={{ position: 'fixed', top: '100px', left: '60px', zIndex: 9999 }}>
          <PlacenameTool map={map} />
          <button className="floating-control-close" onClick={() => setIsPlacenameToolOpen(false)} title="Close Placename Tool">
            &times;
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
};

/** Render the legacy nav "Tools" group (pre-profile behaviour). */
function renderLegacy(
  items: NavItem[],
  onClick: (item: NavItem) => void,
  onClose: () => void,
) {
  return items.map((item) => {
    const key = item.tool ?? item.href ?? item.label;
    if (item.type === 'external' && item.href) {
      return (
        <React.Fragment key={key}>
          <a className="toolbox-tool-button" href={item.href} target="_blank" rel="noopener noreferrer" onClick={onClose}>
            {item.icon && <i className={item.icon} style={{ marginRight: '8px' }} />}
            {item.label}
          </a>
          <p></p>
        </React.Fragment>
      );
    }
    return (
      <React.Fragment key={key}>
        <button className="toolbox-tool-button" onClick={() => onClick(item)}>
          {item.icon && <i className={item.icon} style={{ marginRight: '8px' }} />}
          {item.label}
        </button>
        <p></p>
      </React.Fragment>
    );
  });
}

interface ToolboxEditViewProps {
  catalogue: CatalogueTool[];
  activeProfile: ToolboxProfile;
  /** Whether the active profile can be altered in place (owned, or global when admin). */
  canSaveInPlace: boolean;
  onSaveInPlace: (tools: string[]) => void;
  onRequestSaveAsNew: () => void;
  saveModalSlot: (tools: string[]) => React.ReactNode;
}

/** Imperative handle so the parent's "Done editing" checkmark can save in place. */
export interface ToolboxEditHandle {
  /** Save the current edits in place if allowed and non-empty. Returns whether a save was triggered. */
  save: () => boolean;
}

/** Edit mode: toggle tools on/off, drag to reorder, then save in place / as new / start fresh. */
const ToolboxEditView = forwardRef<ToolboxEditHandle, ToolboxEditViewProps>(({
  catalogue,
  activeProfile,
  canSaveInPlace,
  onSaveInPlace,
  onRequestSaveAsNew,
  saveModalSlot,
}, ref) => {
  const editor = useToolboxEditor(catalogue, activeProfile.profile_data.tools);
  const { dragState, handleDragStart, handleDragEnd, handleDragOver, handleTouchMove, getDragStyles } =
    useDragReorder({ itemIds: editor.items.map((i) => i.tool.key), onReorder: editor.reorder });

  const isEmpty = editor.enabledOrderedKeys.length === 0;

  useImperativeHandle(
    ref,
    () => ({
      save: () => {
        if (canSaveInPlace && !isEmpty) {
          onSaveInPlace(editor.enabledOrderedKeys);
          return true;
        }
        return false;
      },
    }),
    [canSaveInPlace, isEmpty, onSaveInPlace, editor.enabledOrderedKeys],
  );

  return (
    <div className="toolbox-edit">
      <div className="toolbox-edit-hint">
        Editing <strong>{activeProfile.name}</strong> — toggle tools and drag to reorder.
      </div>
      <div className="toolbox-edit-list">
        {editor.items.map((item) => (
          <div
            key={item.tool.key}
            data-layer-id={item.tool.key}
            className={`toolbox-edit-item ${dragState.draggedItemId === item.tool.key ? 'dragging' : ''}`}
            style={getDragStyles(item.tool.key)}
            onMouseEnter={() => handleDragOver(item.tool.key)}
            onMouseUp={handleDragEnd}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleDragEnd}
          >
            <span
              className="toolbox-edit-drag"
              onMouseDown={() => handleDragStart(item.tool.key)}
              onTouchStart={() => handleDragStart(item.tool.key)}
              title="Drag to reorder"
            >
              <FontAwesomeIcon icon={faGripVertical} />
            </span>
            <label className="toolbox-edit-label">
              <input type="checkbox" checked={item.enabled} onChange={() => editor.toggle(item.tool.key)} />
              {item.tool.icon && <i className={item.tool.icon} style={{ margin: '0 8px' }} />}
              {item.tool.label}
            </label>
          </div>
        ))}
      </div>
      <div className="toolbox-edit-actions">
        {canSaveInPlace && (
          <button
            className="toolbox-icon-btn"
            title={`Save changes to ${activeProfile.name}`}
            aria-label={`Save changes to ${activeProfile.name}`}
            onClick={() => onSaveInPlace(editor.enabledOrderedKeys)}
            disabled={isEmpty}
          >
            <FontAwesomeIcon icon={faFloppyDisk} />
          </button>
        )}
        <button
          className="toolbox-icon-btn"
          title="Save as a new toolbox"
          aria-label="Save as a new toolbox"
          onClick={onRequestSaveAsNew}
          disabled={isEmpty}
        >
          <FontAwesomeIcon icon={faFileCirclePlus} />
        </button>
        <button
          className="toolbox-icon-btn"
          title="Start from scratch (clear all)"
          aria-label="Start from scratch (clear all)"
          onClick={editor.clear}
        >
          <FontAwesomeIcon icon={faEraser} />
        </button>
      </div>
      {saveModalSlot(editor.enabledOrderedKeys)}
    </div>
  );
});
ToolboxEditView.displayName = 'ToolboxEditView';

export default ToolBox;
